import { useMutation, useQueryClient } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { FC, useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Button, ButtonSize, ButtonVariant, Hide } from "shared/components";
import { getErrorMessage } from "utils/error";
import { SelectPayment } from "views/rent-reporting/components/SelectPayment";
import {
  AccountStatusVariant,
  PaymentStatuses
} from "views/rent-reporting/constants";
import { Verification } from "views/rent-reporting/types";

type Props = {
  options: { value: string | number; label: JSX.Element }[];
  verificationId: number;
  subscriberId: string | undefined;

  // No Data means that boom never got payment verification from a partner
  // Or PVS couldn't find payment
  isNoData?: boolean;
};

export const SelectAndReport: FC<Props> = ({
  options,
  verificationId,
  subscriberId,
  isNoData
}) => {
  const [selectValue, setSelectValue] = useState<string | number>(
    PaymentStatuses.PAID_ON_TIME
  );

  useEffect(() => {
    if (isNoData) setSelectValue(PaymentStatuses.NO_DATA);
  }, [isNoData]);

  const queryClient = useQueryClient();
  const { mutate: reportSingleRenter } = useMutation({
    mutationKey: ["REPORT_SINGLE_RENTER", subscriberId],
    mutationFn: (data: { verifications: Verification }) => {
      if (isNoData)
        return instance.post(
          `/portal/v1/verifications/${verificationId}/report_on_time`,
          {
            ...data.verifications
          }
        );

      return instance.patch(
        `/portal/v1/customers/${subscriberId}/verifications/${verificationId}`,
        { ...data.verifications }
      );
    },
    onSuccess: () => {
      toast.success("Renter has been successfully reported");
      queryClient.invalidateQueries({
        queryKey: ["SUBSCRIBER_DETAIL", subscriberId]
      });
      queryClient.invalidateQueries({
        queryKey: [
          "portal/v1/customers/all" + "__total",
          { page: 1, per_page: 1, type: AccountStatusVariant.NeedToReport }
        ]
      });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    }
  });

  const handleReportClick = async () => {
    const verifications: Verification = {
      action:
        selectValue === PaymentStatuses.PAID_ON_TIME
          ? PaymentStatuses.PAID_ON_TIME
          : selectValue === PaymentStatuses.NO_DATA
            ? PaymentStatuses.NO_DATA
            : PaymentStatuses.DELINQUENCY,
      months_delinquency:
        selectValue === PaymentStatuses.PAID_ON_TIME ||
        selectValue === PaymentStatuses.NO_DATA
          ? undefined
          : selectValue
    };
    reportSingleRenter({ verifications });
  };

  return (
    <div className="flex gap-4 items-center">
      <SelectPayment
        id={String(subscriberId)}
        value={selectValue || PaymentStatuses.PAID_ON_TIME}
        handleChange={(e) => setSelectValue(e.target.value)}
        options={options}
      />
      <Hide if={isNoData && selectValue === PaymentStatuses.NO_DATA}>
        <Button
          variant={isNoData ? ButtonVariant.BASE : ButtonVariant.PRIMARY}
          borderLess={Boolean(isNoData)}
          size={ButtonSize.XS}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            handleReportClick();
          }}
        >
          {isNoData ? "Update" : "Report"}
        </Button>
      </Hide>
    </div>
  );
};
